import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Plot from 'react-plotly.js'
import { selectUser } from '../../reducers/userSlice'
import { useTheme } from '@mui/styles'
import { getMetricName, getMetricUnits } from './RTFOMetricsPanel'
import { scaleSequential } from 'd3-scale'
import { interpolateTurbo } from 'd3-scale-chromatic'
import SimpleLinearRegression from 'ml-regression-simple-linear'
import PowerRegression from 'ml-regression-power'
import ExponentialRegression from 'ml-regression-exponential'
import { chartColors } from '../../helpers/chartColors'

const markerSymbols = [
  ['circle', 'circle-open', 'circle-dot', 'circle-open-dot'],
  ['square', 'square-open', 'square-dot', 'square-open-dot'],
  ['diamond', 'diamond-open', 'diamond-dot', 'diamond-open-dot'],
  [
    'triangle-up',
    'triangle-up-open',
    'triangle-up-dot',
    'triangle-up-open-dot',
  ],
  [
    'triangle-down',
    'triangle-down-open',
    'triangle-down-dot',
    'triangle-down-open-dot',
  ],
  [
    'triangle-left',
    'triangle-left-open',
    'triangle-left-dot',
    'triangle-left-open-dot',
  ],
  [
    'triangle-right',
    'triangle-right-open',
    'triangle-right-dot',
    'triangle-right-open-dot',
  ],
  [
    'triangle-ne',
    'triangle-ne-open',
    'triangle-ne-dot',
    'triangle-ne-open-dot',
  ],
  [
    'triangle-se',
    'triangle-se-open',
    'triangle-se-dot',
    'triangle-se-open-dot',
  ],
  [
    'triangle-sw',
    'triangle-sw-open',
    'triangle-sw-dot',
    'triangle-sw-open-dot',
  ],
  [
    'triangle-nw',
    'triangle-nw-open',
    'triangle-nw-dot',
    'triangle-nw-open-dot',
  ],
  ['cross', 'cross-open', 'cross-dot', 'cross-open-dot'],
  ['x', 'x-open', 'x-dot', 'x-open-dot'],
  ['pentagon', 'pentagon-open', 'pentagon-dot', 'pentagon-open-dot'],
  ['hexagon', 'hexagon-open', 'hexagon-dot', 'hexagon-open-dot'],
  ['hexagon2', 'hexagon2-open', 'hexagon2-dot', 'hexagon2-open-dot'],
  ['octagon', 'octagon-open', 'octagon-dot', 'octagon-open-dot'],
  ['hexagram', 'hexagram-open', 'hexagram-dot', 'hexagram-open-dot'],
  [
    'star-triangle-up',
    'star-triangle-up-open',
    'star-triangle-up-dot',
    'star-triangle-up-open-dot',
  ],
  [
    'star-triangle-down',
    'star-triangle-down-open',
    'star-triangle-down-dot',
    'star-triangle-down-open-dot',
  ],
  [
    'star-square',
    'star-square-open',
    'star-square-dot',
    'star-square-open-dot',
  ],
  [
    'star-diamond',
    'star-diamond-open',
    'star-diamond-dot',
    'star-diamond-open-dot',
  ],
]

export const RTFODataAnalyticsChart = ({
  displayPads,
  displayWells,
  allPadsMetrics,
  metricX,
  metricY,
  metricZ,
  metricSize,
  zMinMax,
  sizeMinMax,
  xAxisType,
  yAxisType,
  showWellTrendlines,
  showCDF,
}) => {
  const theme = useTheme()
  const user = useSelector(selectUser)

  const darkMode = user?.value?.darkMode

  const data = useMemo(() => {
    return getChartData()

    function getChartData() {
      try {
        if (!allPadsMetrics) return []

        const traces = []

        const colorScale = scaleSequential(interpolateTurbo).domain([
          zMinMax?.[0],
          zMinMax?.[1],
        ])

        const allPads = [
          ...new Set(displayWells?.map((w) => w.split('_')?.[0])),
        ]

        // // Initialize an object to store sum and count for Y values for each stage number
        // const padAverages = {}

        // For each pad, calculate the average then add the trace, even if the wells are not selected
        // Just need the pad check selected to calculate the average of then
        const allPadsNames = allPadsMetrics ? Object.keys(allPadsMetrics) : []
        for (const padName of allPadsNames) {
          const padAverages = {}
          const padIndex = allPadsNames?.indexOf(padName)

          if (!displayPads.includes(padName)) continue
          for (const stageName in allPadsMetrics[padName]?.metrics) {
            const xValue = Number(
              allPadsMetrics[padName]?.metrics?.[stageName]?.[metricX],
            )
            const yValue = Number(
              allPadsMetrics[padName]?.metrics?.[stageName]?.[metricY],
            )

            if (metricX === 'stageNumber' && xValue !== null) {
              // Ignore the initial null placeholder
              if (!padAverages[xValue]) {
                padAverages[xValue] = { sum: 0, count: 0 }
              }
              if (yValue !== null && !isNaN(yValue) && !(yValue < 1)) {
                padAverages[xValue].sum += yValue
                padAverages[xValue].count += 1
              }
            }
          }

          // Calculate the average Y value for each stage number
          const avgX = Object.keys(padAverages).map(Number) // Convert stage numbers back to numbers
          const avgY = avgX.map(
            (x) => padAverages[x].sum / padAverages[x].count,
          )
          // Add a new trace for pad-average points
          traces.push({
            x: avgX,
            y: avgY,
            type: 'scattergl',
            mode: 'markers',
            name: `Pad ${padName} Average`,
            marker: {
              color: 'black', // Choose a distinctive color for pad-average points
              size: 10,
              symbol: markerSymbols?.[padIndex % markerSymbols?.length]?.[0],
            },
          })
        }

        // Calculate traces for each well
        for (const wellIndex in displayWells) {
          const wellName = displayWells[wellIndex]
          const padName = wellName?.split('_')?.[0]
          const padIndex = allPads.indexOf(padName)

          const X = [null]
          const Y = [null]
          const Z = [0]
          const Size = [null]

          let wellStages = allPadsMetrics[padName]?.metrics
            ? Object.keys(allPadsMetrics[padName]?.metrics)
            : []
          wellStages = wellStages.filter((s) => s.includes(wellName))

          const symbols = [
            markerSymbols[parseInt(wellIndex % markerSymbols?.length)]?.[
              parseInt(padIndex % 4)
            ],
          ]

          for (const stageName of wellStages) {
            const xValue = Number(
              allPadsMetrics[padName]?.metrics?.[stageName]?.[metricX],
            )
            const yValue = Number(
              allPadsMetrics[padName]?.metrics?.[stageName]?.[metricY],
            )
            const zValue = Number(
              allPadsMetrics[padName]?.metrics?.[stageName]?.[metricZ],
            )
            const sizeValue =
              Number(
                allPadsMetrics[padName]?.metrics?.[stageName]?.[metricSize],
              ) || 0

            if (
              !isNaN(xValue) &&
              xValue > 0 &&
              !isNaN(yValue) &&
              yValue > 0 &&
              ((!isNaN(zValue) && zValue > 0) || metricZ === 'None')
            ) {
              X.push(xValue)
              Y.push(yValue)
              Z.push(zValue)
              Size.push(sizeValue)
              symbols.push(
                markerSymbols[parseInt(wellIndex % markerSymbols?.length)]?.[
                  parseInt(padIndex % 4)
                ],
              )
            }
            // if (metricX === 'stageNumber' && xValue !== null) {
            //   // Ignore the initial null placeholder
            //   if (!padAverages[xValue]) {
            //     padAverages[xValue] = { sum: 0, count: 0 }
            //   }
            //   if (yValue !== null && !isNaN(yValue) && !(yValue < 1)) {
            //     padAverages[xValue].sum += yValue
            //     padAverages[xValue].count += 1
            //   }
            // }
          }

          if (showCDF === 'show') {
            // Calculate CDF for Y values
            const sortedY = [...Y].sort((a, b) => a - b)
            const Ycdf = sortedY.map(
              (value, index) => (index + 1) / sortedY.length,
            )

            // Add the CDF trace
            traces.push({
              x: sortedY, // 'Y Metric' values as 'X' in the CDF
              y: Ycdf, // CDF probabilities
              xaxis: 'x2', // Plotting against the secondary X axis
              yaxis: 'y2', // Plotting against the secondary Y axis
              type: 'scatter',
              mode: 'lines',
              name: `${wellName} CDF`,
              line: {
                dash: 'dot',
              },
            })
          }

          // add traces
          const wellTrace = {
            x: X,
            y: Y,
            type: 'scattergl',
            mode: 'markers',
            xaxis: 'x1',
            yaxis: 'y1',
            name: wellName,
            text: Z,
            hovertemplate:
              metricZ !== 'None'
                ? `${getMetricName(metricZ)}: %{text:.2f} ${getMetricUnits(metricZ)}<extra></extra>`
                : '',
            marker: {
              color: Z.map((val) => {
                return metricZ === 'None' ? colorScale(0) : colorScale(val)
              }),
              // symbol: [symbols],
              symbol: [...symbols, 'square'],
              // symbol: markerSymbols[parseInt(padIndex)]?.[0],
              size:
                metricSize === 'None' || !sizeMinMax || sizeMinMax?.length === 0
                  ? 10
                  : Size.map((val, i) =>
                      i === 0
                        ? 10
                        : calculateMarkerSize(
                            val,
                            sizeMinMax?.[0],
                            sizeMinMax?.[1],
                          ),
                    ),
              line: {
                width: 1,
                color: '#333',
              },
            },
            // legendgroup: padName
          }

          traces.push(wellTrace)

          if (showWellTrendlines === 'show') {
            // Calculate pad trendline
            const XTrendline = []
            let YTrendline = []
            if (xAxisType === 'log' && yAxisType === 'log') {
              // Power regression
              Y.forEach((val, i) => {
                if (val > 0) {
                  YTrendline.push(val)
                  XTrendline.push(X[i])
                }
              })

              const regression = new PowerRegression(XTrendline, YTrendline)
              YTrendline = XTrendline?.map((x) => regression.predict(x))
            } else if (xAxisType === 'log') {
              // Log regression
              Y.forEach((val, i) => {
                if (val > 0) {
                  YTrendline.push(val)
                  XTrendline.push(X[i])
                }
              })

              const regression = new SimpleLinearRegression(
                XTrendline?.map((val) => Math.log(val)),
                YTrendline,
              )

              YTrendline = XTrendline?.map((x) =>
                regression.predict(Math.log(x)),
              )
            } else if (yAxisType === 'log') {
              // Exponential regression
              Y.forEach((val, i) => {
                if (val > 0) {
                  YTrendline.push(val)
                  XTrendline.push(X[i])
                }
              })

              const regression = new ExponentialRegression(
                XTrendline,
                YTrendline,
              )

              YTrendline = XTrendline?.map((x) => regression.predict(x))
            } else {
              // Linear regression
              Y.forEach((val, i) => {
                if (val > 0) {
                  YTrendline.push(val)
                  XTrendline.push(X[i])
                }
              })

              const regression = new SimpleLinearRegression(
                XTrendline,
                YTrendline,
              )

              YTrendline = XTrendline?.map((x) => regression.predict(x))
            }

            const trendlineTrace = {
              x: XTrendline,
              y: YTrendline,
              type: 'scatter',
              mode: 'lines',
              xaxis: 'x1',
              yaxis: 'y1',
              name: `${wellName} Trendline`,
              line: {
                color: chartColors[parseInt(wellIndex % chartColors?.length)],
                width: 3,
                dash: 'dash',
              },
              // legendgroup: padName
            }
            traces.push(trendlineTrace)
          }
        }

        return traces
      } catch (error) {
        console.log(error)
        return []
      }
    }
  }, [
    allPadsMetrics,
    zMinMax,
    displayPads,
    displayWells,
    metricZ,
    metricSize,
    sizeMinMax,
    showWellTrendlines,
    showCDF,
    metricX,
    metricY,
    xAxisType,
    yAxisType,
  ])

  const layout = useMemo(() => {
    return {
      yaxis: {
        // y metric axis
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        title: {
          text: `${getMetricName(metricY)} ${getMetricUnits(metricY)?.length > 0 ? `(${getMetricUnits(metricY)})` : ''}`,
          font: {
            size: 12,
          },
        },
        anchor: 'x',
        autorange: true,
        rangemode: 'nonnegative',
        side: 'left',
        type: yAxisType === 'log' && 'log',
      },
      xaxis: {
        // x metric axis
        autorange: true,
        rangemode: 'nonnegative',
        title: {
          text: `${getMetricName(metricX)} ${getMetricUnits(metricX)?.length > 0 ? `(${getMetricUnits(metricX)})` : ''}`,
          font: {
            size: 12,
          },
        },
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        // anchor: 'y',
        type: xAxisType === 'log' && 'log',
        // showspikes: true,
        // spikethickness: 2,
        // spikedash: "dot",
        // spikecolor: "#999999",
        // spikemode: "across",
      },
      xaxis2: {
        autorange: true,
        rangemode: 'nonnegative',
        overlaying: 'x',
        anchor: 'y',
        side: 'top',
        title: {
          text: `CDF Y Metric`,
          font: {
            size: 12,
          },
          standoff: 0,
        },
        linecolor: darkMode ? 'lightgrey' : 'black',
        showgrid: false,
        color: darkMode ? 'lightgrey' : 'black',
      },
      yaxis2: {
        rangemode: 'nonnegative',
        overlaying: 'y',
        side: 'right',
        title: {
          text: `CDF Probability`,
          font: {
            size: 12,
          },
        },
        range: [0, 1],
        linecolor: darkMode ? 'lightgrey' : 'black',
        showgrid: false,
        color: darkMode ? 'lightgrey' : 'black',
      },
      // Ensure the grid lines for the primary axes are extended
      // grid: {
      //   rows: 1,
      //   columns: 1,
      //   subplots: [['xy', 'xy2']],
      //   roworder: 'bottom to top',
      // },
      // hovermode: "x",
      // hoverdistance: 10, // Distance to show hover label of data point
      // spikedistance: 1000,
      autosize: true,
      showlegend: true,
      legend: {
        orientation: 'h',
        yanchor: 'top',
        font: {
          color: darkMode ? 'lightgrey' : 'grey',
          size: 10,
        },
        y: -0.15,
      },
      shapes: [],
      annotations: [
        {
          text: 'Data Analytics',
          font: {
            size: 14,
            color: darkMode ? 'lightgrey' : 'black',
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: showCDF === 'show' ? 1.175 : 1.1, //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          text: `Pads: ${displayPads?.join(', ')}`,
          font: {
            size: 10,
            color: darkMode ? 'lightgrey' : 'black',
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: showCDF === 'show' && displayPads?.length > 0 ? 1.13 : 1.06, //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
      ],
      uirevision: true,
      margin: {
        t: showCDF === 'show' && displayPads?.length > 0 ? 90 : 60,
        l: 60,
        r: 60,
        b: 50,
      },
      images: [
        {
          x: 0.035,
          y: 1.025,
          sizex: 0.05,
          sizey: 0.1,
          source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: 'page',
          yref: 'page',
          xanchor: 'right',
          yanchor: 'bottom',
          opacity: 0.75,
        },
      ],
      plot_bgcolor: 'transparent',
      paper_bgcolor:
        theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
    }
  }, [
    darkMode,
    displayPads,
    metricX,
    metricY,
    showCDF,
    theme?.palette?.elevationOne,
    xAxisType,
    yAxisType,
  ])

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: 'RTFO-Data-Analytics' + new Date().toISOString().split('T')[0],
      height: 550,
      width: 960,
      scale: 1, // Multiply title/legend/axis/canvas sizes by this factor
    },
  }

  return (
    <Plot
      style={{height: '100%', width: '100%'}}
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
    />
  )

  function calculateMarkerSize(data, minValue, maxValue) {
    try {
      const sizeMin = 6
      const sizeMax = 30

      // Ensure data is within the min and max range
      data = Math.min(Math.max(data, minValue), maxValue)

      // Calculate the marker size based on the data range
      const sizeRange = sizeMax - sizeMin // Adjust the range to min to max

      if (minValue === maxValue) {
        // If the range is zero, use the mid size
        return (sizeMin + sizeMax) / 2 // Midpoint
      }

      // Calculate the marker size proportionally
      return sizeMin + ((data - minValue) / (maxValue - minValue)) * sizeRange
    } catch (error) {
      console.log(error)
      return 10
    }
  }
}
